import { APP_INITIALIZER, isDevMode, type EnvironmentProviders, type Provider } from '@angular/core';
import { provideTransloco, translocoConfig } from '@jsverse/transloco';
import { LanguageFacade } from './language.facade';
import { APP_LANGUAGES, DEFAULT_LANGUAGE, FALLBACK_LANGUAGE } from './locale.config';
import { TranslocoHttpLoader } from './transloco-loader';

/**
 * Initializes the language by invoking the initialize method of the provided LanguageFacade instance.
 *
 * @param {LanguageFacade} languageFacade - The facade responsible for handling language initialization.
 * @return {function(): Promise<void>} A function that returns a promise resolving once the language initialization is complete.
 */
function initializeLanguage(languageFacade: LanguageFacade): () => Promise<void> {
  return () => languageFacade.initialize();
}

/**
 * An array of providers used to configure internationalization (i18n) settings for the application.
 *
 * This variable includes:
 * - Transloco configuration, specifying available languages, default language, fallback language,
 *   language change behavior, production mode settings, and handling of missing translations.
 * - An application initializer that sets up the initial language state using a factory function.
 *
 * The providers are responsible for enabling and managing i18n features across the application.
 */
export const appI18nProviders: Array<EnvironmentProviders | Provider> = [
  ...provideTransloco({
    config: translocoConfig({
      availableLangs: APP_LANGUAGES.map((language) => language.id),
      defaultLang: DEFAULT_LANGUAGE,
      fallbackLang: FALLBACK_LANGUAGE,
      reRenderOnLangChange: true,
      prodMode: !isDevMode(),
      missingHandler: {
        logMissingKey: isDevMode(),
        useFallbackTranslation: true,
        allowEmpty: false,
      },
      scopes: {
        keepCasing: true,
      },
    }),
    loader: TranslocoHttpLoader,
  }),
  {
    provide: APP_INITIALIZER,
    useFactory: initializeLanguage,
    deps: [LanguageFacade],
    multi: true,
  },
];
